import { Controller } from "@hotwired/stimulus"

// Renders the `ask_user` clarifying prompt as a Claude/Cursor-style card: one
// question at a time, numbered options, company/folder SelectComponents, a
// "Something else" free-text choice, Skip, and pager navigation. On submit it
// dispatches `hudini-questions:answered` with the composed answer text; the
// parent `hudini` controller sends it as the next message so the assistant can
// continue.
export default class extends Controller {
  static outlets = ["hudini"]
  static values = {
    data: Object,
    interactive: { type: Boolean, default: true },
    answered: { type: Boolean, default: false },
    // Survives Turbo reconnects on the permanent dock: current index + answers.
    progress: { type: Object, default: {} },
    defaults: { type: Object, default: {} },
    companyPickerUrl: { type: String, default: "" },
    folderPickerUrl: { type: String, default: "" },
  }

  connect() {
    const data = this.dataValue || {}
    this.items = Array.isArray(data.items) ? data.items : []
    this.boundKeydown = this.onKeydown.bind(this)

    if (this.items.length === 0) {
      this.element.remove()
      return
    }

    // Turbo Drive disconnects/reconnects Stimulus on `data-turbo-permanent`
    // nodes (the Hudini dock). Without DOM-persisted answered/progress state,
    // reconnect would re-dock the wizard from question 1 after the user already
    // finished — or wipe mid-wizard answers.
    if (this.alreadyAnswered()) {
      this.answered = true
      this.interactiveValue = false
      this.host = this.element
      if (!this.element.querySelector(".hudini-questions__recap")) {
        this.renderStatic()
      }
      return
    }

    this.restoreProgress()
    this.seedPickerDefaults()

    if (this.interactiveValue) {
      // Dock the active prompt into the footer (replacing the composer) so it
      // reads as the next thing to act on, rather than scrolling away inline.
      this.dockToFooter()
      // The card lives outside this controller's DOM subtree once docked, so
      // Stimulus `data-action` wiring wouldn't reach it. Delegate events on the
      // host element instead (works for both docked and inline rendering).
      this.attachHostListeners()
      // Capture phase so we own Escape (skip) before the surrounding sidepanel's
      // document listener can close the whole panel.
      document.addEventListener("keydown", this.boundKeydown, true)
      this.render()
    } else {
      this.host = this.element
      this.renderStatic()
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown, true)
    // Restore the composer (and detach delegated listeners) if we're torn down
    // before answering, e.g. switching chats or a new prompt replacing this one.
    // When already answered, submit() handled this.
    if (!this.answered) this.releaseFooter()
    else this.detachHostListeners()
  }

  alreadyAnswered() {
    return this.answeredValue || !!this.element.querySelector(".hudini-questions__recap")
  }

  restoreProgress() {
    const progress = this.progressValue || {}
    const savedAnswers = progress.answers
    this.answers = (savedAnswers && typeof savedAnswers === "object" && !Array.isArray(savedAnswers))
      ? { ...savedAnswers }
      : {}
    const savedIndex = Number(progress.index)
    this.index = Number.isInteger(savedIndex) && savedIndex >= 0 ? savedIndex : 0
    if (this.index >= this.items.length) this.index = Math.max(this.items.length - 1, 0)
    this.answered = false
    this.otherActive = false
  }

  persistProgress() {
    this.progressValue = { index: this.index, answers: this.answers }
  }

  // --- Footer docking --------------------------------------------------------

  // Renders the interactive card in the footer dock (owned by the `hudini`
  // controller, reached via an outlet) and hides the composer. Falls back to
  // inline rendering if the outlet/dock isn't available for any reason.
  dockToFooter() {
    const dock = this.hasHudiniOutlet ? this.hudiniOutlet.dockElement : null

    if (dock) {
      this.host = dock
      this.hudiniOutlet.dockQuestions()
    } else {
      this.host = this.element
    }
  }

  // Clears the dock and brings the composer back. Safe to call repeatedly.
  releaseFooter() {
    this.detachHostListeners()
    if (this.hasHudiniOutlet) this.hudiniOutlet.releaseDock()
    this.host = this.element
  }

  // --- Delegated event handling ----------------------------------------------

  attachHostListeners() {
    if (!this.host || this.listenerHost === this.host) return

    this.boundHostClick = this.onHostClick.bind(this)
    this.boundHostInput = this.onHostInput.bind(this)
    this.boundHostInputKeydown = this.onHostInputKeydown.bind(this)
    this.boundHostChange = this.onHostChange.bind(this)
    this.host.addEventListener("click", this.boundHostClick)
    this.host.addEventListener("input", this.boundHostInput)
    this.host.addEventListener("keydown", this.boundHostInputKeydown)
    this.host.addEventListener("change", this.boundHostChange)
    this.listenerHost = this.host
  }

  detachHostListeners() {
    if (!this.listenerHost) return

    this.listenerHost.removeEventListener("click", this.boundHostClick)
    this.listenerHost.removeEventListener("input", this.boundHostInput)
    this.listenerHost.removeEventListener("keydown", this.boundHostInputKeydown)
    this.listenerHost.removeEventListener("change", this.boundHostChange)
    this.listenerHost = null
  }

  onHostClick(event) {
    const el = event.target.closest("[data-q]")
    if (!el || !this.host.contains(el) || el.tagName === "INPUT") return
    if (el.disabled) return

    const synthetic = { currentTarget: el, preventDefault() {} }
    switch (el.dataset.q) {
      case "choose": return this.choose(synthetic)
      case "openOther": return this.openOther(synthetic)
      case "skip": return this.skip(synthetic)
      case "advance": return this.advance(synthetic)
      case "prev": return this.prev(synthetic)
      case "next": return this.next(synthetic)
    }
  }

  onHostInput(event) {
    const el = event.target
    if (el.dataset.q === "otherInput") this.otherInput({ currentTarget: el })
    else if (el.dataset.q === "freeInput") this.freeInput({ currentTarget: el })
  }

  onHostInputKeydown(event) {
    if (event.target.dataset.input === "other") this.otherKeydown(event)
  }

  onHostChange(event) {
    if (!event.target.closest("[data-q='picker']")) return
    this.capturePickerAnswer()
    this.syncAdvanceButton()
  }

  // --- Rendering -------------------------------------------------------------

  get current() {
    return this.items[this.index]
  }

  isPickerQuestion(item = this.current) {
    return item?.input_type === "company" || item?.input_type === "folder"
  }

  render() {
    const item = this.current
    const multiple = item.allow_multiple === true
    const picker = this.isPickerQuestion(item)
    const options = picker ? [] : (Array.isArray(item.options) ? item.options : [])
    const selected = this.answers[item.id]

    const rows = options.map((option, i) => {
      const isSelected = multiple
        ? Array.isArray(selected) && selected.includes(option)
        : selected === option
      return `
        <button type="button" class="hudini-questions__row ${isSelected ? "hudini-questions__row--selected" : ""}" data-value="${this.attr(option)}" data-q="choose">
          <span class="hudini-questions__num">${i + 1}</span>
          <span class="hudini-questions__row-label">${this.escape(option)}</span>
          <span class="hudini-questions__row-check"><i class="far fa-check"></i></span>
        </button>`
    }).join("")

    const otherActive = this.otherActive || (selected != null && options.length > 0 && !this.valueInOptions(selected, options))
    const otherValue = otherActive && typeof selected === "string" && !options.includes(selected) ? selected : ""
    const otherRow = options.length > 0 ? `
      <div class="hudini-questions__row hudini-questions__row--other ${otherActive ? "hudini-questions__row--open" : ""}">
        <button type="button" class="hudini-questions__other-toggle" data-q="openOther">
          <span class="hudini-questions__num"><i class="far fa-pen"></i></span>
          <span class="hudini-questions__row-label hudini-questions__row-label--muted">Something else</span>
        </button>
        <input type="text" class="hudini-questions__other-input ${otherActive ? "" : "hidden"}" placeholder="Type your answer…" value="${this.attr(otherValue)}" data-q="otherInput" data-input="other" />
        <button type="button" class="button button--unfilled button--small hudini-questions__skip-inline" data-q="skip">Skip</button>
      </div>` : ""

    const freeText = !picker && options.length === 0 ? `
      <input type="text" class="hudini-questions__text" placeholder="Type your answer…" value="${this.attr(typeof selected === "string" ? selected : "")}" data-q="freeInput" data-input="other" />` : ""

    const pickerMount = picker ? `
      <div class="hudini-questions__picker" data-q="picker" data-picker-type="${this.attr(item.input_type)}">
        <div class="hudini-questions__picker-loading">Loading…</div>
      </div>` : ""

    const sendLabel = this.index === this.items.length - 1 ? "Send" : "Next"
    const canAdvance = this.hasCurrentAnswer()

    this.host.innerHTML = `
      <div class="hudini-questions__card">
        <div class="hudini-questions__head">
          <div class="hudini-questions__title">${this.escape(item.question)}</div>
          <div class="hudini-questions__pager">
            <button type="button" class="button button--icon button--xsmall button--unfilled" data-q="prev" ${this.index === 0 ? "disabled" : ""}><i class="far fa-chevron-left"></i></button>
            <span class="hudini-questions__count">${this.index + 1} of ${this.items.length}</span>
            <button type="button" class="button button--icon button--xsmall button--unfilled" data-q="next" ${this.index === this.items.length - 1 ? "disabled" : ""}><i class="far fa-chevron-right"></i></button>
          </div>
        </div>
        <div class="hudini-questions__list">
          ${rows}
          ${otherRow}
          ${freeText}
          ${pickerMount}
        </div>
        <div class="hudini-questions__foot">
          <span class="hudini-questions__hint">Enter to ${sendLabel === "Send" ? "send" : "continue"} · Esc to skip</span>
          <div class="hudini-questions__actions">
            <button type="button" class="button button--unfilled button--small" data-q="skip">Skip</button>
            <button type="button" class="button button--primary button--small" data-q="advance" ${canAdvance ? "" : "disabled"}>${sendLabel}</button>
          </div>
        </div>
      </div>`

    if (picker) {
      this.mountPicker()
      return
    }

    // Focus the free-text input when it's the only way to answer, or when the
    // user has opened "Something else".
    if (otherActive || options.length === 0) {
      const input = this.host.querySelector("[data-input='other']")
      if (input) input.focus()
    }
  }

  renderStatic() {
    const rows = this.items.map(item => `<li>${this.escape(item.question)}</li>`).join("")
    this.host.innerHTML = `
      <div class="hudini-questions__card hudini-questions__card--static">
        <ul class="hudini-questions__static">${rows}</ul>
      </div>`
  }

  renderAnswered(text) {
    const safe = text ? this.escape(text).replace(/\n/g, "<br>") : "Skipped"
    // The recap always lands inline in the thread, even when the prompt was
    // docked in the footer.
    this.element.innerHTML = `
      <div class="hudini-questions__card hudini-questions__card--static">
        <div class="hudini-questions__recap">${safe}</div>
      </div>`
  }

  // --- Company / folder pickers ----------------------------------------------

  seedPickerDefaults() {
    const defaults = this.defaultsValue || {}
    this.items.forEach((item) => {
      // Don't overwrite answers restored after a Turbo reconnect.
      if (this.answers[item.id] != null) return
      if (item.input_type === "company" && defaults.company) {
        this.answers[item.id] = item.allow_multiple
          ? [defaults.company]
          : defaults.company
      }
    })
    // Folder after company so selectedCompanyId() sees the seeded company.
    this.items.forEach((item) => {
      if (item.input_type !== "folder") return
      if (this.answers[item.id] != null) return
      if (!defaults.folder) return
      this.answers[item.id] = this.folderCompatibleWithCompany(defaults.folder)
        ? defaults.folder
        : { id: "none", name: "No folder" }
    })
    this.persistProgress()
  }

  folderCompatibleWithCompany(folder) {
    if (!folder || folder.id === "none") return true
    if (folder.company_id == null) return true
    const companyId = this.selectedCompanyId()
    const expected = companyId === "global" ? "" : String(companyId)
    return String(folder.company_id) === expected
  }

  companyQuestion() {
    return this.items.find((item) => item.input_type === "company")
  }

  selectedCompanyId() {
    const companyItem = this.companyQuestion()
    if (!companyItem) return this.defaultsValue?.company?.id || "global"

    const answer = this.answers[companyItem.id]
    if (Array.isArray(answer)) return answer[0]?.id || "global"
    if (answer && typeof answer === "object") return answer.id || "global"
    return this.defaultsValue?.company?.id || "global"
  }

  async mountPicker() {
    const item = this.current
    const mount = this.host.querySelector("[data-q='picker']")
    if (!mount) return

    const url = this.pickerUrlFor(item)
    if (!url) {
      mount.innerHTML = `<div class="hudini-questions__picker-error">Picker unavailable</div>`
      return
    }

    try {
      const response = await fetch(url, {
        headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin",
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      mount.innerHTML = await response.text()
      // TomSelect finishes init on the next tick; capture the prefilled value then.
      requestAnimationFrame(() => {
        this.capturePickerAnswer()
        this.syncAdvanceButton()
      })
    } catch (_error) {
      mount.innerHTML = `<div class="hudini-questions__picker-error">Could not load selector</div>`
    }
  }

  pickerUrlFor(item) {
    if (item.input_type === "company") {
      if (!this.companyPickerUrlValue) return null
      const url = new URL(this.companyPickerUrlValue, window.location.origin)
      url.searchParams.set("name", item.id)
      if (item.allow_multiple) url.searchParams.set("multiple", "true")
      const selected = this.answers[item.id]
      const values = Array.isArray(selected) ? selected : (selected ? [selected] : [])
      values.forEach((value, index) => {
        if (!value?.id) return
        url.searchParams.append(`selected[][id]`, value.id)
        url.searchParams.append(`selected[][name]`, value.name || "")
        // Keep a simple selected_ids fallback for single-select.
        if (index === 0 && !item.allow_multiple) url.searchParams.set("selected_ids[]", value.id)
      })
      return url.toString()
    }

    if (item.input_type === "folder") {
      if (!this.folderPickerUrlValue) return null
      const url = new URL(this.folderPickerUrlValue, window.location.origin)
      url.searchParams.set("name", item.id)
      const companyId = this.selectedCompanyId()
      url.searchParams.set("company_id", companyId === "global" ? "" : companyId)

      const selected = this.answers[item.id]
      // Drop a folder default that belongs to a different company than the one chosen.
      if (selected?.id && selected.id !== "none") {
        const folderCompany = selected.company_id
        if (folderCompany != null && String(folderCompany) !== String(companyId === "global" ? "" : companyId)) {
          url.searchParams.set("selected_id", "none")
        } else {
          url.searchParams.set("selected_id", selected.id)
        }
      } else {
        url.searchParams.set("selected_id", selected?.id || "none")
      }
      return url.toString()
    }

    return null
  }

  capturePickerAnswer() {
    const item = this.current
    if (!this.isPickerQuestion(item)) return

    const select = this.host.querySelector("[data-q='picker'] select")
    if (!select) return

    const tom = select.tomselect
    const ids = tom ? [...tom.items] : Array.from(select.selectedOptions).map((option) => option.value)

    const entries = ids.map((id) => {
      const option = tom?.options?.[id]
      return {
        id: String(id),
        name: option?.name || select.querySelector(`option[value="${CSS.escape(String(id))}"]`)?.textContent || String(id),
      }
    })

    if (item.input_type === "folder") {
      const entry = entries[0] || { id: "none", name: "No folder" }
      const companyId = this.selectedCompanyId()
      this.answers[item.id] = {
        ...entry,
        company_id: companyId === "global" ? "" : String(companyId),
      }
      this.persistProgress()
      return
    }

    // Company: single object or array for multi-select.
    if (item.allow_multiple) this.answers[item.id] = entries
    else this.answers[item.id] = entries[0] || { id: "global", name: "Central KB" }

    // Changing company invalidates a folder pick from another company.
    this.invalidateFolderIfCompanyMismatch()
    this.persistProgress()
  }

  invalidateFolderIfCompanyMismatch() {
    const folderItem = this.items.find((item) => item.input_type === "folder")
    if (!folderItem) return

    const folder = this.answers[folderItem.id]
    if (!folder || this.folderCompatibleWithCompany(folder)) return
    this.answers[folderItem.id] = { id: "none", name: "No folder" }
  }

  // --- Interaction -----------------------------------------------------------

  choose(event) {
    const value = event.currentTarget.dataset.value
    const item = this.current
    this.otherActive = false

    if (item.allow_multiple === true) {
      const current = Array.isArray(this.answers[item.id]) ? this.answers[item.id].slice() : []
      const at = current.indexOf(value)
      if (at >= 0) current.splice(at, 1)
      else current.push(value)
      this.answers[item.id] = current
      this.persistProgress()
      this.render()
    } else {
      this.answers[item.id] = value
      this.persistProgress()
      this.advance()
    }
  }

  openOther(event) {
    event.preventDefault()
    this.otherActive = true
    this.render()
  }

  otherInput(event) {
    const item = this.current
    const value = event.currentTarget.value.trim()
    if (value) this.answers[item.id] = value
    else if (!item.allow_multiple) delete this.answers[item.id]
    this.persistProgress()
    this.syncAdvanceButton()
  }

  freeInput(event) {
    const item = this.current
    const value = event.currentTarget.value.trim()
    if (value) this.answers[item.id] = value
    else delete this.answers[item.id]
    this.persistProgress()
    this.syncAdvanceButton()
  }

  otherKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.advance()
    } else if (event.key === "Escape") {
      event.preventDefault()
      this.skip()
    }
  }

  // Advances only once the current question has an answer. Skip bypasses that
  // via `{ force: true }` so empty questions can still be dismissed.
  advance(event) {
    if (event && typeof event.preventDefault === "function") event.preventDefault()
    if (!(event && event.force) && !this.hasCurrentAnswer()) return

    if (this.index < this.items.length - 1) {
      this.otherActive = false
      this.index += 1
      this.persistProgress()
      this.render()
    } else {
      this.submit()
    }
  }

  skip(event) {
    if (event) event.preventDefault()
    delete this.answers[this.current.id]
    this.persistProgress()
    this.advance({ force: true })
  }

  prev(event) {
    if (event) event.preventDefault()
    if (this.index === 0) return
    this.otherActive = false
    this.index -= 1
    this.persistProgress()
    this.render()
  }

  next(event) {
    if (event) event.preventDefault()
    if (this.index >= this.items.length - 1) return
    this.otherActive = false
    this.index += 1
    this.persistProgress()
    this.render()
  }

  submit() {
    if (this.answered) return
    this.answered = true
    this.answeredValue = true
    this.interactiveValue = false
    this.progressValue = {}
    document.removeEventListener("keydown", this.boundKeydown, true)

    const text = this.compose()
    this.releaseFooter()
    this.renderAnswered(text)
    this.dispatch("answered", { detail: { text } })
  }

  // --- Helpers ---------------------------------------------------------------

  hasCurrentAnswer() {
    const answer = this.answers[this.current.id]
    if (answer == null) return false
    if (Array.isArray(answer)) return answer.length > 0
    if (typeof answer === "object") return answer.id != null && String(answer.id).length > 0
    return String(answer).trim().length > 0
  }

  syncAdvanceButton() {
    const button = this.host && this.host.querySelector("[data-q='advance']")
    if (button) button.disabled = !this.hasCurrentAnswer()
  }

  compose() {
    const lines = []
    this.items.forEach(item => {
      const answer = this.answers[item.id]
      if (answer == null) return
      const value = this.formatAnswer(item, answer)
      if (value && value.trim()) lines.push(`${item.question} → ${value.trim()}`)
    })
    return lines.length ? lines.join("\n") : "No specific preferences — use your best judgment."
  }

  formatAnswer(item, answer) {
    if (Array.isArray(answer)) {
      return answer.map((entry) => this.formatAnswer(item, entry)).filter(Boolean).join(", ")
    }

    if (answer && typeof answer === "object") {
      if (item.input_type === "company") {
        return answer.id === "global"
          ? "Central KB (company_id: none)"
          : `${answer.name} (company_id: ${answer.id})`
      }
      if (item.input_type === "folder") {
        return answer.id === "none"
          ? "No folder"
          : `${answer.name} (folder_id: ${answer.id})`
      }
      return answer.name || String(answer.id)
    }

    return String(answer)
  }

  valueInOptions(value, options) {
    if (Array.isArray(value)) return value.every(v => options.includes(v))
    return options.includes(value)
  }

  onKeydown(event) {
    if (this.answered || !this.interactiveValue) return
    if (!this.isLatest()) return

    const tag = (event.target.tagName || "").toLowerCase()
    const typing = tag === "input" || tag === "textarea" || tag === "select"
      || event.target.closest(".ts-wrapper, .ts-dropdown, [data-q='picker']")
    if (typing) return

    if (event.key === "Escape") {
      event.preventDefault()
      // Own Escape so the sidepanel's document handler doesn't also close the
      // whole panel; skipping the question is the active prompt's job.
      event.stopPropagation()
      this.skip()
    } else if (event.key === "Enter") {
      event.preventDefault()
      this.advance()
    } else if (event.key === "ArrowRight") {
      event.preventDefault()
      this.next()
    } else if (event.key === "ArrowLeft") {
      event.preventDefault()
      this.prev()
    } else if (/^[1-9]$/.test(event.key)) {
      if (this.isPickerQuestion()) return
      const options = Array.isArray(this.current.options) ? this.current.options : []
      const i = parseInt(event.key, 10) - 1
      if (i < options.length) {
        event.preventDefault()
        this.answers[this.current.id] = this.current.allow_multiple ? this.toggleInArray(this.current.id, options[i]) : options[i]
        if (this.current.allow_multiple) this.render()
        else this.advance()
      }
    }
  }

  toggleInArray(id, value) {
    const current = Array.isArray(this.answers[id]) ? this.answers[id].slice() : []
    const at = current.indexOf(value)
    if (at >= 0) current.splice(at, 1)
    else current.push(value)
    return current
  }

  // True when this is the most recent questions card in the thread, so global
  // key handling only applies to the active prompt.
  isLatest() {
    const all = document.querySelectorAll(".hudini-questions")
    return all.length === 0 || all[all.length - 1] === this.element
  }

  escape(value) {
    const div = document.createElement("div")
    div.textContent = value == null ? "" : String(value)
    return div.innerHTML
  }

  attr(value) {
    return this.escape(value).replace(/"/g, "&quot;")
  }
}
